import { useEffect } from "react";
import { getMetaData } from "../../utils/Constant";

const MKVFormatGuide = () => {

  // Set title and metadata
  useEffect(() => {
    const metaData = getMetaData("mkv-meta-data");

    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description;
  }, []);

  return (
    <div className="container mt-5 mb-5">
      <article className="bg-white rounded-4 shadow-lg border-0 overflow-hidden" lang="en">
        {/* Header Section */}
        <header
          className="text-center py-5 px-3 position-relative"
          style={{
            background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
            color: "#fff",
          }}
        >
          <h1 className="display-5 fw-bold mb-3 position-relative">
            MKV Format: Why Video Enthusiasts Love It
          </h1>
          <p className="lead mb-0 position-relative fs-4">
            Exploring the Swiss Army knife of video containers that can handle it all
          </p>
          <div className="mt-4 position-relative">
            <span className="badge bg-light text-primary fs-6 me-2 p-2">
              Matroska Container
            </span>
            <span className="badge bg-light text-primary fs-6 me-2 p-2">
              Feature Rich
            </span>
            <span className="badge bg-light text-primary fs-6 p-2">
              Open Source
            </span>
          </div>
        </header>

        {/* Content Section */}
        <section className="p-4 p-md-5">
          {/* Introduction */}
          <div
            className="alert alert-primary border-0 rounded-3 shadow-sm"
            role="alert"
          >
            <div className="d-flex">
              <div className="me-3 fs-4" aria-label="Toolbox">🧰</div>
              <div>
                <p className="fs-5 lh-lg mb-0 text-dark">
                  Ever downloaded a movie only to find it has multiple subtitle tracks, commentary audio, 
                  and chapter markers all in one file? You've probably encountered MKV - the format that 
                  refuses to compromise. Think of it as the luxury SUV of video containers: it might not 
                  be the smallest or most common, but it can carry everything you throw at it.
                </p>
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div className="alert alert-info border-0 rounded-3 mb-5">
            <div className="d-flex">
              <div className="me-3 fs-5" aria-label="Light bulb">💡</div>
              <div className="text-dark">
                <strong>What MKV actually is:</strong> MKV (Matroska Video) is an open-source container format 
                that's like a digital Swiss Army knife. It doesn't care what codecs you use, how many audio tracks 
                you include, or how many subtitle languages you want to pack in. If you can imagine it, MKV can probably contain it.
              </div>
            </div>
          </div>

          {/* Quick Reality Check */}
          <div className="alert alert-success border-0 rounded-3 mb-5">
            <div className="d-flex">
              <div className="me-3 fs-5" aria-label="Star">⭐</div>
              <div className="text-dark">
                <strong>Why enthusiasts love MKV:</strong> While MP4 is like a reliable sedan that gets you from A to B, 
                MKV is the custom-built RV with all the amenities. It's the go-to choice for movie collectors, anime fans, 
                and anyone who wants their media organized exactly how they like it.
              </div>
            </div>
          </div>

          {/* Quick Pros & Cons */}
          <div className="row g-4 mb-5">
            <div className="col-md-6">
              <div className="card border-success border-2 h-100 shadow-sm">
                <div className="card-header bg-success text-white">
                  <h5 className="mb-0">
                    <span aria-label="Thumbs up">👍</span> Why MKV Shines
                  </h5>
                </div>
                <div className="card-body">
                  <ul className="text-dark">
                    <li><strong>Unlimited features</strong> - tracks, subtitles, chapters</li>
                    <li><strong>Any codec works</strong> - no format restrictions</li>
                    <li><strong>Open standard</strong> - free forever, no royalties</li>
                    <li><strong>Error recovery</strong> - handles corruption gracefully</li>
                    <li><strong>Future-proof</strong> - easily extendable format</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-danger border-2 h-100 shadow-sm">
                <div className="card-header bg-danger text-white">
                  <h5 className="mb-0">
                    <span aria-label="Thumbs down">👎</span> Where MKV Struggles
                  </h5>
                </div>
                <div className="card-body">
                  <ul className="text-dark">
                    <li><strong>Limited native support</strong> on some devices</li>
                    <li><strong>Potentially larger file sizes</strong> with multiple embedded tracks</li>
                    <li><strong>Limited DRM support</strong> - can be applied externally but not built-in</li>
                    <li><strong>Editing complexity</strong> - not as simple as MP4</li>
                    <li><strong>Streaming challenges</strong> - not optimized for web delivery</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="card border-0 shadow-sm mb-5">
            <div className="card-header bg-primary text-white py-3">
              <h2 className="h4 mb-0 fw-bold">What we'll explore together</h2>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "What makes MKV special",
                      "The open-source advantage", 
                      "Technical superpowers",
                      "MKV vs MP4 showdown",
                      "Why enthusiasts love it",
                      "Where you'll find MKV",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 1}`}
                          className="text-decoration-none text-primary hover-text-dark fw-medium"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "Compatibility realities",
                      "Creating your own MKV files",
                      "When to choose MKV",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 7}`}
                          className="text-decoration-none text-primary hover-text-dark fw-medium"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <div id="section-1" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 1"
              >
                1
              </span>
              What makes MKV the power user's choice
            </h2>
            <p className="text-dark fs-5">
              MKV isn't just another video format - it's a philosophy. Where other formats say "no, that's too complicated," 
              MKV says "why not both?" It's the format that believes your movie experience should include director's commentary, 
              multiple subtitle languages, and chapter markers, all in one tidy package.
            </p>
            <div className="row g-3 mt-3">
              {[
                {
                  icon: "🎭",
                  text: "Unlimited Tracks",
                  desc: "As many audio, video, subtitle tracks as you want",
                  alt: "Theater masks representing multiple tracks"
                },
                {
                  icon: "🔓",
                  text: "Open Source", 
                  desc: "Free forever, developed by the community",
                  alt: "Open lock representing open source"
                },
                {
                  icon: "🌍",
                  text: "Codec Agnostic",
                  desc: "Works with virtually any video or audio codec",
                  alt: "Globe representing universal codec support"
                },
                {
                  icon: "📚",
                  text: "Rich Features", 
                  desc: "Chapters, menus, metadata, attachments",
                  alt: "Books representing rich features"
                },
                {
                  icon: "🛡️",
                  text: "Error Resilient",
                  desc: "Handles file corruption gracefully",
                  alt: "Shield representing error resilience"
                },
                {
                  icon: "🚀",
                  text: "Future Proof",
                  desc: "Easily extended for new technologies",
                  alt: "Rocket representing future proof"
                },
              ].map((item, index) => (
                <div key={index} className="col-6 col-md-4 col-lg-2">
                  <div className="p-3 border rounded-3 text-center bg-light hover-lift transition-all h-100">
                    <div className="fs-2 mb-2" aria-label={item.alt}>{item.icon}</div>
                    <div className="small fw-bold text-dark">{item.text}</div>
                    <div className="text-muted small mt-1">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 2 */}
          <div id="section-2" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 2"
              >
                2
              </span>
              The open-source advantage: Why freedom matters
            </h2>
            <div className="row g-4">
              {[
                {
                  icon: "💸",
                  title: "No Royalties",
                  desc: "Completely free to use - no licensing fees ever",
                  alt: "Money with wings representing no royalties"
                },
                {
                  icon: "👥",
                  title: "Community Driven", 
                  desc: "Developed by enthusiasts for enthusiasts",
                  alt: "People representing community"
                },
                {
                  icon: "🔧",
                  title: "Total Control", 
                  desc: "You own your media, no corporate restrictions",
                  alt: "Wrench representing control"
                },
                {
                  icon: "📖",
                  title: "Transparent",
                  desc: "Open specification means no hidden limitations",
                  alt: "Book representing transparency"
                },
              ].map((item, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-start p-3 border rounded-3 bg-light h-100 shadow-sm">
                    <span className="fs-2 me-3" aria-label={item.alt}>{item.icon}</span>
                    <div>
                      <h5 className="fw-bold mb-1 text-dark">{item.title}</h5>
                      <p className="text-muted mb-0">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="alert alert-info mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3" aria-label="History book">📚</div>
                <div className="text-dark">
                  <strong>A brief history lesson:</strong> Matroska started in 2002 as a community response to proprietary 
                  formats. The name comes from Russian nesting dolls (matryoshka), representing how the format can contain 
                  multiple elements within one file. It's been developed by volunteers who believe media should be free and accessible to all.
                </div>
              </div>
            </div>
          </div>

          {/* Section 3 - Technical Superpowers */}
          <div id="section-3" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 3"
              >
                3
              </span>
              MKV's technical superpowers: What it can really do
            </h2>
            <p className="text-muted mb-4">
              <em>MKV isn't just flexible - it's practically limitless in what it can contain. Here's what makes it so powerful.</em>
            </p>
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm" aria-label="MKV technical capabilities">
                <thead className="table-primary">
                  <tr>
                    <th className="border-0">Feature</th>
                    <th className="border-0">What It Means</th>
                    <th className="border-0">Real-World Benefit</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      feature: "Multiple Video Tracks",
                      meaning: "Different angles, versions, or resolutions in one file",
                      benefit: "Perfect for multi-angle content or different quality versions"
                    },
                    {
                      feature: "Unlimited Audio Tracks", 
                      meaning: "As many audio streams as you need",
                      benefit: "Multiple languages, commentary tracks, audio descriptions"
                    },
                    {
                      feature: "Advanced Subtitles",
                      meaning: "Support for advanced subtitle formats like ASS/SSA",
                      benefit: "Animated subtitles, karaoke effects, precise positioning"
                    },
                    {
                      feature: "Chapter Markers", 
                      meaning: "Navigate through content like a DVD",
                      benefit: "Skip to specific scenes or sections easily"
                    },
                    {
                      feature: "File Attachments",
                      meaning: "Embed fonts, images, or other files",
                      benefit: "Ensure subtitles display correctly with embedded fonts"
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-bold text-dark">{row.feature}</td>
                      <td className="text-muted">{row.meaning}</td>
                      <td className="text-dark">{row.benefit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="alert alert-light mt-3 border" role="alert">
              <small className="text-dark">
                <strong>The anime connection:</strong> MKV became hugely popular in the anime community because it handles 
                advanced subtitle formats (ASS/SSA) that support karaoke effects, animated text, and precise positioning - 
                things that are essential for fansubs but impossible in basic formats like MP4.
              </small>
            </div>
          </div>

          {/* Section 4 - MKV vs MP4 */}
          <div id="section-4" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 4"
              >
                4
              </span>
              MKV vs MP4: The ultimate showdown
            </h2>
            
            <div className="row g-4">
              <div className="col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white py-3">
                    <h5 className="mb-0 fw-bold">
                      <span aria-label="MKV logo">⚫</span> MKV - The Power User
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-dark">
                      <li><strong>Unlimited tracks</strong> of any type</li>
                      <li><strong>Any codec</strong> supported</li>
                      <li><strong>Advanced features</strong> like chapters, menus</li>
                      <li><strong>Open source</strong> - no restrictions</li>
                      <li><strong>Error resilient</strong> structure</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-success me-2">Feature Rich</span>
                      <span className="badge bg-success">Flexible</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-primary text-white py-3">
                    <h5 className="mb-0 fw-bold">
                      <span aria-label="MP4 logo">🔷</span> MP4 - The Universal Standard
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-dark">
                      <li><strong>Limited tracks</strong> (usually 1 video, few audio)</li>
                      <li><strong>Restricted codecs</strong> (H.264, H.265, AAC)</li>
                      <li><strong>Basic features</strong> only</li>
                      <li><strong>Universal compatibility</strong> across devices</li>
                      <li><strong>Streaming optimized</strong> for web delivery</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-primary me-2">Universal</span>
                      <span className="badge bg-primary">Optimized</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="alert alert-info mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3" aria-label="Scale">⚖️</div>
                <div className="text-dark">
                  <strong>The simple choice:</strong> Use MP4 when you need maximum compatibility (streaming, mobile devices, sharing). 
                  Use MKV when you need advanced features (multiple audio tracks, advanced subtitles, chapters) or are working with 
                  niche codecs. They're different tools for different jobs!
                </div>
              </div>
            </div>
          </div>

          {/* Section 5 - Why Enthusiasts Love It */}
          <div id="section-5" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 5"
              >
                5
              </span>
              Why media enthusiasts swear by MKV
            </h2>
            <div className="row g-4">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white py-3">
                    <h5 className="mb-0 fw-bold">
                      <span aria-label="Movie collection">🎬</span> Movie Collectors
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-dark">
                      <li><strong>Director's commentary</strong> tracks</li>
                      <li><strong>Multiple subtitle</strong> languages</li>
                      <li><strong>Chapter markers</strong> for scene selection</li>
                      <li><strong>Behind-the-scenes</strong> content</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-info text-white py-3">
                    <h5 className="mb-0 fw-bold">
                      <span aria-label="Anime">🇯🇵</span> Anime Fans
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-dark">
                      <li><strong>Advanced subtitle</strong> effects</li>
                      <li><strong>Multiple audio</strong> tracks (sub/dub)</li>
                      <li><strong>Clean openings</strong> and endings</li>
                      <li><strong>Font embedding</strong> for consistency</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-warning text-dark py-3">
                    <h5 className="mb-0 fw-bold">
                      <span aria-label="Video editor">🎮</span> Content Creators
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-dark">
                      <li><strong>Lossless codec</strong> support</li>
                      <li><strong>Multiple versions</strong> in one file</li>
                      <li><strong>Future-proof</strong> archiving</li>
                      <li><strong>Flexible workflow</strong> options</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="alert alert-warning mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3" aria-label="Pro tip">💡</div>
                <div className="text-dark">
                  <strong>The archiving advantage:</strong> MKV is perfect for creating personal media archives because it preserves 
                  everything in one file. No more hunting for separate subtitle files or losing track of commentary tracks. Your 
                  entire movie experience stays neatly packaged together forever.
                </div>
              </div>
            </div>
          </div>

          {/* Section 6 - Where MKV Shines */}
          <div id="section-6" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 6"
              >
                6
              </span>
              Where you'll find MKV in the wild
            </h2>
            
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm" aria-label="Common uses of MKV format">
                <thead className="table-success">
                  <tr>
                    <th className="border-0">Use Case</th>
                    <th className="border-0">Why MKV is Perfect Here</th>
                    <th className="border-0">Typical Features Used</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      useCase: "Anime Releases",
                      reason: "Handles advanced subtitles and multiple audio tracks",
                      features: "ASS/SSA subtitles, Japanese/English audio, chapters"
                    },
                    {
                      useCase: "Movie Backups", 
                      reason: "Preserves all DVD/Blu-ray features in one file",
                      features: "Multiple audio tracks, subtitles, chapter markers"
                    },
                    {
                      useCase: "Live Recordings",
                      reason: "Supports variable frame rate perfectly",
                      features: "VFR video, multiple audio sources"
                    },
                    {
                      useCase: "Niche Content", 
                      reason: "Works with obscure or new codecs",
                      features: "Any video/audio codec, future-proof"
                    },
                    {
                      useCase: "Personal Archives",
                      reason: "Everything stays together in one file",
                      features: "Attachments, metadata, multiple versions"
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-bold text-dark">{row.useCase}</td>
                      <td className="text-muted">{row.reason}</td>
                      <td>
                        <span className="badge bg-primary text-white">{row.features}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="alert alert-success mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3" aria-label="Professional tip">💼</div>
                <div className="text-dark">
                  <strong>The rule of thumb:</strong> If you're downloading content from enthusiast communities (anime, movie collectors, 
                  etc.), you'll probably encounter MKV. If you're streaming from major services (Netflix, YouTube, etc.), you'll get MP4. 
                  They serve different audiences with different needs.
                </div>
              </div>
            </div>
          </div>

          {/* Section 7 - Compatibility Realities */}
          <div id="section-7" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 7"
              >
                7
              </span>
              Will it play? MKV compatibility in 2024
            </h2>
            <div className="row g-3">
              {[
                {
                  text: "Computers: Excellent - VLC, MPC-HC, and most media players handle MKV perfectly",
                  alt: "Number 1"
                },
                {
                  text: "Smart TVs: Good - Most modern smart TVs support MKV, but advanced features may not work", 
                  alt: "Number 2"
                },
                {
                  text: "Gaming Consoles: Mixed - PlayStation and Xbox have limited MKV support",
                  alt: "Number 3"
                },
                {
                  text: "Mobile Devices: Improving - Many Android players support MKV, iOS is more limited",
                  alt: "Number 4"
                },
                {
                  text: "Streaming Devices: Varies - Some (NVIDIA Shield) are great, others (Roku) are basic",
                  alt: "Number 5"
                },
                {
                  text: "Web Browsers: Limited - No native HTML5 support, requires conversion for web use",
                  alt: "Number 6"
                },
              ].map((tip, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-center p-3 border rounded-3 bg-light h-100">
                    <div
                      className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ width: "30px", height: "30px" }}
                      aria-label={tip.alt}
                    >
                      {index + 1}
                    </div>
                    <span className="fw-medium text-dark">{tip.text}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-4 bg-light rounded-3">
              <h5 className="fw-bold mb-3 text-dark">The VLC Solution:</h5>
              <div className="row">
                <div className="col-md-6">
                  <ul className="text-dark">
                    <li><strong>VLC Media Player:</strong> Plays most MKV files with broad codec support</li>
                    <li><strong>Free and open source:</strong> No cost, no restrictions</li>
                    <li><strong>Cross-platform:</strong> Windows, Mac, Linux, mobile</li>
                    <li><strong>Automatic codec handling:</strong> Minimal setup required</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="text-dark">
                    <li><strong>Advanced features:</strong> Supports most MKV capabilities</li>
                    <li><strong>Streaming:</strong> Can play MKV over network</li>
                    <li><strong>Conversion:</strong> Built-in tools to convert to other formats</li>
                    <li><strong>Regular updates:</strong> Always improving compatibility</li>
                  </ul>
                </div>
              </div>
              <div className="mt-3 p-3 bg-info text-white rounded">
                <div className="d-flex">
                  <div className="me-3" aria-label="Information">ℹ️</div>
                  <div>
                    <small>
                      <strong>The codec matters more than the container:</strong> Often when people say "MKV doesn't play on my device," 
                      the real issue is the video codec inside the MKV container. A MKV with H.264 video will play in more places than 
                      a MKV with AV1 video, regardless of the container format. Some exotic codecs or corrupted files may require additional 
                      codec installation or configuration adjustments even in VLC.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 8 - Creating MKV Files */}
          <div id="section-8" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 8"
              >
                8
              </span>
              Creating your own MKV files: Tools and tips
            </h2>
            
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm" aria-label="Tools for working with MKV files">
                <thead className="table-info">
                  <tr>
                    <th className="border-0">Tool</th>
                    <th className="border-0">Best For</th>
                    <th className="border-0">Key Features</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      tool: "MKVToolNix",
                      purpose: "Professional MKV creation and editing",
                      features: "GUI and command line, batch processing, advanced features"
                    },
                    {
                      tool: "HandBrake", 
                      purpose: "Converting videos to MKV",
                      features: "User-friendly, good presets, basic MKV features"
                    },
                    {
                      tool: "FFmpeg",
                      purpose: "Command-line power users",
                      features: "Maximum control, scripting, any codec support"
                    },
                    {
                      tool: "MakeMKV", 
                      purpose: "Ripping Blu-rays and DVDs",
                      features: "Preserves all features, easy to use"
                    },
                    {
                      tool: "VLC Media Player",
                      purpose: "Quick conversions and playback",
                      features: "Built-in converter, plays most formats"
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-bold text-dark">{row.tool}</td>
                      <td className="text-muted">{row.purpose}</td>
                      <td className="text-dark">{row.features}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Quick MKV Creation Guide */}
            <div className="mt-4 p-4 bg-light rounded-3">
              <h5 className="fw-bold mb-3 text-dark">Quick MKV Creation with MKVToolNix:</h5>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white">
                    <h6 className="fw-bold text-dark">
                      <span aria-label="Step 1">1️⃣</span> Add Your Source Files
                    </h6>
                    <p className="mb-0 small text-dark">Drag video, audio, and subtitle files into MKVToolNix</p>
                    <small className="text-muted">It automatically detects track types and languages</small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white">
                    <h6 className="fw-bold text-dark">
                      <span aria-label="Step 2">2️⃣</span> Configure Tracks
                    </h6>
                    <p className="mb-0 small text-dark">Set default tracks, languages, and track names</p>
                    <small className="text-muted">Make it easy to navigate your final file</small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white">
                    <h6 className="fw-bold text-dark">
                      <span aria-label="Step 3">3️⃣</span> Add Chapters (Optional)
                    </h6>
                    <p className="mb-0 small text-dark">Create chapter markers for easy navigation</p>
                    <small className="text-muted">Great for movies, courses, or long videos</small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white">
                    <h6 className="fw-bold text-dark">
                      <span aria-label="Step 4">4️⃣</span> Start Muxing
                    </h6>
                    <p className="mb-0 small text-dark">Click "Start muxing" to create your MKV file</p>
                    <small className="text-muted">Fast process since it's just repackaging</small>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="alert alert-success mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3" aria-label="Key">🔑</div>
                <div className="text-dark">
                  <strong>Remember "muxing":</strong> Creating MKV files is usually about "muxing" (multiplexing) - taking existing 
                  video, audio, and subtitle tracks and packaging them together. This is different from "encoding" which recompresses 
                  the video. Muxing is fast and lossless since it doesn't change the actual video quality.
                </div>
              </div>
            </div>
          </div>

          {/* Section 9 - When to Choose MKV */}
          <div id="section-9" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 9"
              >
                9
              </span>
              When should you choose MKV? A practical guide
            </h2>
            
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm" aria-label="When to use MKV versus other formats">
                <thead className="table-info">
                  <tr>
                    <th className="border-0">Situation</th>
                    <th className="border-0">Choose MKV?</th>
                    <th className="border-0">Why & Alternatives</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      situation: "Personal movie collection",
                      chooseMkv: "✅ Yes",
                      reasoning: "Perfect for preserving all features and tracks"
                    },
                    {
                      situation: "YouTube upload", 
                      chooseMkv: "❌ No",
                      reasoning: "Use MP4 - better compatibility and streaming"
                    },
                    {
                      situation: "Anime with advanced subtitles",
                      chooseMkv: "✅ Yes", 
                      reasoning: "MKV handles ASS/SSA subtitles that MP4 can't"
                    },
                    {
                      situation: "Mobile device playback",
                      chooseMkv: "⚠️ Maybe", 
                      reasoning: "Only if your player supports it, otherwise use MP4"
                    },
                    {
                      situation: "Archiving rare codecs",
                      chooseMkv: "✅ Yes",
                      reasoning: "MKV supports any codec, perfect for preservation"
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-bold text-dark">{row.situation}</td>
                      <td>
                        <span className={row.chooseMkv === '✅ Yes' ? 'text-success fw-bold' : row.chooseMkv === '❌ No' ? 'text-danger fw-bold' : 'text-warning fw-bold'}>
                          {row.chooseMkv}
                        </span>
                      </td>
                      <td className="text-muted">{row.reasoning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="alert alert-success mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3" aria-label="Key">🔑</div>
                <div className="text-dark">
                  <strong>The golden rule:</strong> Choose MKV when features matter more than universal compatibility. Choose MP4 when 
                  you need your video to play everywhere with minimal fuss. They're both excellent formats that serve different purposes - 
                  the trick is knowing which tool to use for which job.
                </div>
              </div>
            </div>
          </div>

          {/* Section 10 - FAQ */}
          {/* <div id="section-10" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 10"
              >
                10
              </span>
              Your MKV questions answered
            </h2>
            
            <div className="accordion" id="mkvFAQ">
              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                    Why do anime fans use MKV so much compared to other formats?
                  </button>
                </h3>
                <div id="faq1" className="accordion-collapse collapse" data-bs-parent="#mkvFAQ">
                  <div className="accordion-body">
                    <p><strong>MKV is basically the perfect format for anime. Here's why:</strong></p>
                    <ul className="text-dark">
                      <li><strong>Advanced subtitles:</strong> Anime often uses ASS/SSA subtitles with karaoke effects, animations, and precise positioning that MP4 can't handle</li>
                      <li><strong>Multiple audio tracks:</strong> Fans want both Japanese audio with subtitles and English dubs in the same file</li>
                      <li><strong>Font embedding:</strong> MKV can include custom fonts to ensure subtitles look exactly as intended</li>
                      <li><strong>Chapter markers:</strong> Easy navigation between opening, episode, ending, and preview sections</li>
                      <li><strong>Community standards:</strong> The anime fan community standardized on MKV early on</li>
                    </ul>
                    <p className="text-dark"><strong>Basically, MKV respects the hard work that fansub groups put into making beautiful subtitles.</strong></p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                    Can I play MKV files on my iPhone or iPad?
                  </button>
                </h3>
                <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#mkvFAQ">
                  <div className="accordion-body">
                    <p><strong>Yes, but you'll need the right app. Here are your options:</strong></p>
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <strong className="text-dark">Great Options:</strong>
                        <ul className="text-dark">
                          <li><strong>VLC for Mobile:</strong> Free, plays most MKV files</li>
                          <li><strong>Infuse:</strong> Beautiful interface, great features (subscription)</li>
                          <li><strong>nPlayer:</strong> Powerful player with network streaming</li>
                          <li><strong>Outplayer:</strong> Good for subtitles and network shares</li>
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <strong className="text-dark">Limitations:</strong>
                        <ul className="text-dark">
                          <li>Native iOS video player doesn't support MKV</li>
                          <li>Some advanced features may not work perfectly</li>
                          <li>File transfer can be awkward compared to MP4</li>
                          <li>Battery usage might be higher than native formats</li>
                        </ul>
                      </div>
                    </div>
                    <p className="mt-3 text-dark"><strong>My recommendation:</strong> Install VLC for Mobile (it's free!) and you'll be able to play most MKV files without issues.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                    Is there any quality difference between MKV and MP4?
                  </button>
                </h3>
                <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#mkvFAQ">
                  <div className="accordion-body">
                    <p><strong>No - and this is really important to understand:</strong></p>
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <strong className="text-dark">Containers vs Codecs:</strong>
                        <ul className="text-dark">
                          <li><strong>MKV and MP4 are containers</strong> - like boxes</li>
                          <li><strong>H.264, H.265, AV1 are codecs</strong> - like the actual video inside</li>
                          <li>You can have the exact same video quality in both formats</li>
                          <li>The container just determines what features are available</li>
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <strong className="text-dark">Real Differences:</strong>
                        <ul className="text-dark">
                          <li><strong>File size:</strong> MKV might be slightly larger with multiple embedded tracks</li>
                          <li><strong>Features:</strong> MKV supports more advanced options</li>
                          <li><strong>Compatibility:</strong> MP4 plays in more places natively</li>
                          <li><strong>Flexibility:</strong> MKV works with more codecs</li>
                        </ul>
                      </div>
                    </div>
                    <p className="mt-3 text-dark"><strong>The video quality is determined by the codec and bitrate, not the container.</strong> A high-quality H.265 encode will look the same whether it's in MKV or MP4. File size differences come from the number of tracks and features, not the container format itself.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq4">
                    Does MKV support DRM like MP4 does?
                  </button>
                </h3>
                <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#mkvFAQ">
                  <div className="accordion-body">
                    <p><strong>This is an important distinction:</strong></p>
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <strong className="text-dark">MKV DRM Reality:</strong>
                        <ul className="text-dark">
                          <li><strong>No built-in DRM:</strong> MKV format doesn't have native DRM support</li>
                          <li><strong>External DRM possible:</strong> Can be applied through third-party solutions</li>
                          <li><strong>Not industry standard:</strong> Major streaming services prefer MP4 with Widevine/PlayReady</li>
                          <li><strong>Community focus:</strong> MKV prioritizes openness over copy protection</li>
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <strong className="text-dark">MP4 Advantage:</strong>
                        <ul className="text-dark">
                          <li><strong>Built-in DRM:</strong> Designed with commercial streaming in mind</li>
                          <li><strong>Industry standard:</strong> Netflix, Amazon, Disney+ all use MP4 with DRM</li>
                          <li><strong>Hardware support:</strong> DRM systems integrated into devices</li>
                          <li><strong>Content protection:</strong> Meets studio requirements for new releases</li>
                        </ul>
                      </div>
                    </div>
                    <p className="mt-3 text-dark"><strong>Bottom line:</strong> MKV can technically have DRM applied externally, but it's not the format's focus. Most commercial streaming services use MP4 because it has robust, standardized DRM systems built in. MKV's strength is flexibility and features, while MP4's strength is broad compatibility and commercial readiness.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq5">
                    Should I convert my existing video collection to MKV?
                  </button>
                </h3>
                <div id="faq5" className="accordion-collapse collapse" data-bs-parent="#mkvFAQ">
                  <div className="accordion-body">
                    <p><strong>It depends on your goals. Here's when it makes sense:</strong></p>
                    <ul className="text-dark">
                      <li><strong>Yes, if:</strong> You want to combine separate subtitle files with your videos</li>
                      <li><strong>Yes, if:</strong> You're archiving and want the most future-proof format</li>
                      <li><strong>Yes, if:</strong> You have multiple audio tracks or commentary to preserve</li>
                      <li><strong>Maybe, if:</strong> You mainly watch on computers and want the best features</li>
                      <li><strong>No, if:</strong> You primarily stream to phones, tablets, or smart TVs</li>
                      <li><strong>No, if:</strong> Your current MP4 files work fine and you don't need new features</li>
                    </ul>
                    <p className="text-dark"><strong>The conversion process:</strong> Use tools like MKVToolNix to "remux" (repackage) your existing videos without losing quality. This is different from re-encoding which can reduce quality.</p>
                    <p className="text-dark"><strong>For most people:</strong> Only convert videos where you actually need MKV's advanced features. There's no need to convert everything "just because."</p>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Final CTA */}
          <div
            className="text-center mt-5 p-5 rounded-4"
            style={{
              background: "linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)",
            }}
          >
            <h2 className="h1 fw-bold mb-3 text-primary">
              Ready to explore the power of MKV?
            </h2>
            <p className="fs-5 text-dark mb-4">
              Get sample MKV files to test playback or see the advanced features in action.
            </p>
            <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
              <a
                href="https://samplefiles.dev/sample-videos/sample-mkv"
                className="btn btn-primary btn-lg px-5 py-3 shadow hover-lift"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span aria-label="Film projector">🎬</span> Get MKV Sample Files
              </a>
              <a
                href="#section-1"
                className="btn btn-outline-primary btn-lg px-5 py-3"
              >
                <span aria-label="Book">📖</span> Read This Again
              </a>
            </div>
            <p className="mt-3 text-muted small">
              Various MKV files with multiple tracks, subtitles, and features - perfect for testing players or learning about this powerful format.
            </p>
          </div>
        </section>
      </article>
    </div>
  );
};

export default MKVFormatGuide;

//                      "Your MKV questions answered",
